import { apiClient } from '../lib/apiClient';

/**
 * PAR surveys (lighting module "Lichtkarte"): PAR measured at points on a tank
 * photo, corals placed on it, and the placement assessment the API computes on
 * every read (src/services/lighting/parMap.ts). Positions are 0–1 of the photo.
 */
export const ZONES = ['top', 'middle', 'bottom'] as const;
export type Zone = (typeof ZONES)[number];

export const LIGHT_GROUPS = ['soft', 'lps', 'sps', 'nps'] as const;
export type LightGroup = (typeof LIGHT_GROUPS)[number];

export type PlacementStatus = 'ok' | 'too_low' | 'too_high' | 'uncertain' | 'unknown_group' | 'not_applicable' | 'no_data';
export type Confidence = 'high' | 'medium' | 'low';

export interface LightSurvey {
  readonly id: string;
  readonly aquariumId: string;
  readonly title: string;
  readonly measuredAt: string;
  readonly imageId: string | null;
  readonly imageUrl: string | null;
  readonly imageWidth: number | null;
  readonly imageHeight: number | null;
  readonly sensor: string | null;
  readonly intensityPercent: number | null;
  readonly photoperiodHours: number | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface LightSurveySummary extends LightSurvey {
  readonly pointCount: number;
  readonly coralCount: number;
}

export interface LightPoint {
  readonly id: string;
  readonly sequence: number;
  readonly x: number;
  readonly y: number;
  readonly par: number;
  readonly zone: Zone | null;
}

export interface LightCoral {
  readonly id: string;
  readonly livestockId: string | null;
  readonly livestockName: string | null;
  readonly scientificName: string | null;
  readonly label: string | null;
  readonly lightGroup: LightGroup | null;
  readonly group: LightGroup | null;
  readonly x: number;
  readonly y: number;
  readonly zone: Zone | null;
}

export interface CoralAssessment {
  readonly coralId: string;
  readonly group: LightGroup | null;
  readonly minPar: number | null;
  readonly maxPar: number | null;
  readonly status: PlacementStatus;
  readonly estimatedPar: number | null;
  readonly confidence: Confidence | null;
  readonly nearestPointId: string | null;
  readonly dli: number | null;
  readonly suggestion: { readonly pointId: string; readonly par: number; readonly direction: 'higher' | 'lower' | 'sideways' } | null;
}

export interface GroupSpots {
  readonly group: LightGroup;
  readonly minPar: number;
  readonly maxPar: number;
  readonly pointIds: string[];
}

export interface GridCell {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly par: number;
}

export interface LightAssessment {
  readonly intensityPercent: number | null;
  readonly simulated: boolean;
  readonly scale: number;
  readonly points: Array<{ readonly id: string; readonly par: number; readonly dli: number | null }>;
  readonly corals: CoralAssessment[];
  readonly spots: GroupSpots[];
  readonly grid: GridCell[];
  readonly source: string;
}

export interface LightSurveyDetail extends LightSurvey {
  readonly points: LightPoint[];
  readonly corals: LightCoral[];
  readonly assessment: LightAssessment;
}

export interface SurveyInput {
  title?: string;
  measured_at?: string;
  image_id?: string | null;
  sensor?: string | null;
  intensity_percent?: number | null;
  photoperiod_hours?: number | null;
  notes?: string | null;
}

export interface PointInput {
  x?: number;
  y?: number;
  par?: number;
  zone?: Zone | null;
}

export interface CoralInput {
  x?: number;
  y?: number;
  zone?: Zone | null;
  livestock_id?: string | null;
  label?: string | null;
  light_group?: LightGroup | null;
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/light-surveys`;
export const lightSurveysKey = (aquariumId: string) => ['light-surveys', aquariumId] as const;
export const lightSurveyKey = (aquariumId: string, surveyId: string, intensity?: number) =>
  ['light-surveys', aquariumId, surveyId, intensity ?? null] as const;

/** Band of a PAR value for the overlay colours and the legend (≈ the coral groups' ranges). */
export type ParBand = 'low' | 'medium' | 'high' | 'very_high';
export function parBand(par: number): ParBand {
  if (par < 75) return 'low';
  if (par < 200) return 'medium';
  if (par <= 400) return 'high';
  return 'very_high';
}

export function formatSurveyDate(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

/** A, B, …, Z, AA, AB — the label of a measured point. */
export function pointLabel(sequence: number): string {
  let n = Math.max(1, Math.floor(sequence));
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

export async function listLightSurveys(aquariumId: string): Promise<LightSurveySummary[]> {
  const res = await apiClient.get<{ data: LightSurveySummary[] }>(base(aquariumId));
  return Array.isArray(res?.data) ? res.data : [];
}

export async function getLightSurvey(aquariumId: string, surveyId: string, intensity?: number): Promise<LightSurveyDetail> {
  const query = intensity !== undefined ? `?intensity=${encodeURIComponent(String(intensity))}` : '';
  const res = await apiClient.get<{ data: LightSurveyDetail }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}${query}`);
  return res.data;
}

export async function createLightSurvey(aquariumId: string, input: SurveyInput & { title: string }): Promise<LightSurveyDetail> {
  const res = await apiClient.post<{ data: LightSurveyDetail }>(base(aquariumId), input);
  return res.data;
}

export async function updateLightSurvey(aquariumId: string, surveyId: string, input: SurveyInput): Promise<LightSurveyDetail> {
  const res = await apiClient.patch<{ data: LightSurveyDetail }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}`, input);
  return res.data;
}

export async function deleteLightSurvey(aquariumId: string, surveyId: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/${encodeURIComponent(surveyId)}`);
}

export async function addLightPoint(aquariumId: string, surveyId: string, input: Required<Pick<PointInput, 'x' | 'y' | 'par'>> & PointInput): Promise<LightSurveyDetail> {
  const res = await apiClient.post<{ data: LightSurveyDetail }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}/points`, input);
  return res.data;
}

export async function updateLightPoint(aquariumId: string, surveyId: string, pointId: string, input: PointInput): Promise<LightSurveyDetail> {
  const res = await apiClient.patch<{ data: LightSurveyDetail }>(
    `${base(aquariumId)}/${encodeURIComponent(surveyId)}/points/${encodeURIComponent(pointId)}`,
    input,
  );
  return res.data;
}

export async function deleteLightPoint(aquariumId: string, surveyId: string, pointId: string): Promise<LightSurveyDetail> {
  const res = await apiClient.delete<{ data: LightSurveyDetail }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}/points/${encodeURIComponent(pointId)}`);
  return res.data;
}

export async function addLightCoral(aquariumId: string, surveyId: string, input: Required<Pick<CoralInput, 'x' | 'y'>> & CoralInput): Promise<LightSurveyDetail> {
  const res = await apiClient.post<{ data: LightSurveyDetail }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}/corals`, input);
  return res.data;
}

export async function updateLightCoral(aquariumId: string, surveyId: string, coralId: string, input: CoralInput): Promise<LightSurveyDetail> {
  const res = await apiClient.patch<{ data: LightSurveyDetail }>(
    `${base(aquariumId)}/${encodeURIComponent(surveyId)}/corals/${encodeURIComponent(coralId)}`,
    input,
  );
  return res.data;
}

export async function deleteLightCoral(aquariumId: string, surveyId: string, coralId: string): Promise<LightSurveyDetail> {
  const res = await apiClient.delete<{ data: LightSurveyDetail }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}/corals/${encodeURIComponent(coralId)}`);
  return res.data;
}

// ─── Lighting evaluation agent (AI) ──────────────────────────────────────────
// src/services/lighting/lightEvaluationService.ts: the overlay picture, the
// photo and the measurement series judged by the AI; stored per survey state.

/** A survey needs this many measured points before the AI can evaluate it (MIN_POINTS_FOR_EVALUATION). */
export const MIN_POINTS_FOR_EVALUATION = 3;

export type LightVerdict = 'adequate' | 'too_low' | 'too_high' | 'uneven' | 'insufficient_data';
export type AreaAssessment = 'too_low' | 'adequate' | 'too_high';
export type CoralPlacement = 'well_placed' | 'move_higher' | 'move_lower' | 'move_sideways' | 'uncertain';
export type VisualCondition = 'healthy' | 'pale_or_bleached' | 'dark_or_browning' | 'tissue_recession' | 'polyps_closed' | 'not_visible';
export type RecommendationAction = 'decrease_intensity' | 'increase_intensity' | 'adjust_photoperiod' | 'move_coral' | 'measure_more' | 'other';
export type Priority = 'high' | 'medium' | 'low';

export interface LightZoneStats {
  readonly band: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
  readonly pointLabels: readonly string[];
  readonly minPar: number;
  readonly medianPar: number;
  readonly maxPar: number;
  readonly suits: ReadonlyArray<'soft' | 'lps' | 'sps'>;
}

/** src/services/lighting/lightZones.ts: centre/sides and reef top/middle/sand bed of a survey. */
export interface LightZones {
  readonly horizontal: readonly LightZoneStats[];
  readonly levels: readonly LightZoneStats[];
  readonly sideToCenter: number | null;
  readonly bottomToTop: number | null;
  readonly expectedGradient: boolean;
  readonly primeSpsLabels: readonly string[];
}

export interface LightEvaluationResult {
  readonly verdict: LightVerdict;
  readonly summary: string;
  readonly confidence: Confidence;
  readonly areas: ReadonlyArray<{
    readonly area: string;
    readonly assessment: AreaAssessment;
    /** Care groups the zone suits (absent on evaluations before v2). */
    readonly suitableGroups?: ReadonlyArray<'soft' | 'lps' | 'sps'>;
    readonly pointLabels: readonly string[];
    readonly detail: string;
  }>;
  readonly corals: ReadonlyArray<{
    readonly coralNumber: number;
    readonly coralId: string;
    readonly name: string;
    readonly placement: CoralPlacement;
    readonly visualCondition: VisualCondition;
    readonly comment: string;
  }>;
  readonly recommendations: ReadonlyArray<{ readonly action: RecommendationAction; readonly priority: Priority; readonly text: string }>;
  /** Next lamp intensity step in percentage points (±10 at most); null = keep. */
  readonly intensityChangePercent: number | null;
  readonly intensityStepCapped: boolean;
  /** The AI suggested a lamp change the measurements do not support; it was dropped (v2+). */
  readonly intensityStepWithheld?: boolean;
  /** Computed light zones, not AI (v2+). */
  readonly zones?: LightZones | null;
  readonly usedPhoto: boolean;
  readonly usedOverlay: boolean;
  readonly pointCount: number;
  readonly coralCount: number;
}

export interface LightEvaluation {
  readonly id: string;
  readonly surveyId: string;
  readonly aquariumId: string;
  readonly surveyTitle: string;
  readonly surveyMeasuredAt: string;
  /** Language the texts are written in (en, de, …). */
  readonly language: string;
  readonly provider: string;
  readonly model: string;
  readonly createdAt: string;
  /** The survey changed after this evaluation. */
  readonly outdated: boolean;
  readonly result: LightEvaluationResult;
}

export const lightEvaluationKey = (aquariumId: string, surveyId: string) => ['light-surveys', aquariumId, surveyId, 'evaluation'] as const;
export const latestLightEvaluationKey = (aquariumId: string) => ['light-surveys', aquariumId, 'evaluations', 'latest'] as const;

export async function getLightEvaluation(aquariumId: string, surveyId: string): Promise<LightEvaluation | null> {
  const res = await apiClient.get<{ data: LightEvaluation | null }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}/evaluation`);
  return res?.data ?? null;
}

export async function getLatestLightEvaluation(aquariumId: string): Promise<LightEvaluation | null> {
  const res = await apiClient.get<{ data: LightEvaluation | null }>(`${base(aquariumId)}/evaluations/latest`);
  return res?.data ?? null;
}

/**
 * Runs the lighting evaluation agent; the stored one comes back for an unchanged survey unless
 * `force`. null = still being generated (202): it is stored when done, poll getLightEvaluation.
 */
export async function evaluateLightSurvey(aquariumId: string, surveyId: string, force = false): Promise<LightEvaluation | null> {
  const res = await apiClient.post<{ data: LightEvaluation | null }>(`${base(aquariumId)}/${encodeURIComponent(surveyId)}/evaluation`, force ? { force: true } : {});
  return res.data ?? null;
}

/**
 * The overlay picture as a large JPEG (2400–4096 px, labels without overlaps,
 * see-through): GET …/overlay.jpg. `estimate` false leaves out the estimated
 * area; `intensity` shows a simulated lamp intensity.
 */
export async function downloadOverlayImage(
  aquariumId: string,
  survey: Pick<LightSurvey, 'id' | 'title' | 'measuredAt'>,
  options: { estimate: boolean; intensity?: number },
): Promise<{ blob: Blob; filename: string }> {
  const { blob, filename } = await apiClient.getBlob(`${base(aquariumId)}/${encodeURIComponent(survey.id)}/overlay.jpg`, {
    params: { estimate: options.estimate ? undefined : '0', intensity: options.intensity },
  });
  const slug = survey.title.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'survey';
  return { blob, filename: filename ?? `par-map-${slug}-${survey.measuredAt.slice(0, 10)}.jpg` };
}

/** Offers a Blob to the browser as a file download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke after the click has been handled (some browsers need the URL a moment longer).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
